import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { factValue, useContractFactsQuery } from '@/lib/api/contract-facts';
import {
  contractValueToDollars,
  currentMonthPeriod,
  useCreateBudgetMutation,
  useCreateBudgetEntryMutation,
  validateBudgetEntryForm,
  type BudgetEntryFormErrors,
} from '@/lib/api/health-budget';

/**
 * Budget write forms for Health Check → Budget (T-0226; Phase-1 wiring
 * moved out of the route so they render and test without the router).
 *
 *   - `BudgetSetupCard` creates the baseline. It pre-fills the contract
 *     amount, start and end from the approved contract facts
 *     (`contract_value`, `contract_start_date`, `contract_end_date`) when
 *     the engagement has them; every field stays editable (C2-26).
 *   - `AddBudgetEntryCard` records change-order deltas and actual spend.
 *     Actual spend needs a period (the API rejects it otherwise), so the
 *     two date fields appear for that layer, default to the current month,
 *     and say what is wrong inline instead of bouncing a 400 (C2-09).
 *
 * The prototype (`screens-modules-tabs.js → Screens.healthBudget`) has no
 * write forms; these follow the same card / label / input tokens the
 * existing health cards use.
 */

const inputClass = 'h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2';
const inputErrorClass = 'border-posture-critical';

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  if (!message) return null;
  return (
    <span id={id} role="alert" className="mt-1 block text-meta text-posture-critical">
      {message}
    </span>
  );
}

export function BudgetSetupCard({ engagementId }: { engagementId: string }) {
  const create = useCreateBudgetMutation(engagementId);
  const facts = useContractFactsQuery(engagementId);
  const [amount, setAmount] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [prefilled, setPrefilled] = useState<string[]>([]);
  // Pre-fill once, when the facts first arrive, and never again: a later
  // refetch must not overwrite what the person typed.
  const appliedFacts = useRef(false);
  useEffect(() => {
    if (appliedFacts.current || !facts.data) return;
    appliedFacts.current = true;
    const applied: string[] = [];
    const value = contractValueToDollars(factValue(facts.data, 'contract_value'));
    if (value !== null) {
      setAmount(String(value));
      applied.push('contract value');
    }
    const factStart = factValue(facts.data, 'contract_start_date');
    if (factStart) {
      setStart(factStart);
      applied.push('contract start');
    }
    const factEnd = factValue(facts.data, 'contract_end_date');
    if (factEnd) {
      setEnd(factEnd);
      applied.push('contract end');
    }
    setPrefilled(applied);
  }, [facts.data]);

  const amountNum = Number(amount);
  const canSubmit = Number.isFinite(amountNum) && amountNum > 0 && !create.isPending;
  return (
    <section
      aria-labelledby="budget-setup-title"
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
    >
      <h2 id="budget-setup-title" className="t-h3">
        Set up the budget baseline
      </h2>
      <p className="mt-1 text-meta text-ink-500">
        The original contract amount from the SOW; period of performance is optional but powers the
        plan curve.
      </p>
      {prefilled.length > 0 ? (
        <p className="mt-1 text-meta text-ink-500" data-testid="budget-prefill-note">
          Pre-filled from the approved contract facts ({prefilled.join(', ')}). Edit anything that
          differs from the SOW.
        </p>
      ) : null}
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <label className="text-body-sm">
          <span className="mb-1 block text-meta text-ink-500">Original contract amount ($)</span>
          <input
            type="number"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputClass}
            placeholder="e.g. 250000"
          />
        </label>
        <label className="text-body-sm">
          <span className="mb-1 block text-meta text-ink-500">Contract start</span>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="text-body-sm">
          <span className="mb-1 block text-meta text-ink-500">Contract end</span>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className={inputClass}
          />
        </label>
      </div>
      {create.error ? <ErrorMessage error={create.error} className="mt-2" /> : null}
      <div className="mt-3 flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={!canSubmit}
          onClick={() =>
            create.mutate({
              original_contract_amount: amountNum,
              contract_start_date: start || null,
              contract_end_date: end || null,
            })
          }
        >
          {create.isPending ? <Spinner /> : null}
          Create budget
        </Button>
      </div>
    </section>
  );
}

export function AddBudgetEntryCard({ engagementId }: { engagementId: string }) {
  const create = useCreateBudgetEntryMutation(engagementId);
  const [layer, setLayer] = useState<'actual_spend' | 'change_order_delta'>('actual_spend');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [period, setPeriod] = useState(() => currentMonthPeriod());
  // Messages appear after the person has touched a field or tried to
  // submit, so an untouched default form does not open covered in red.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [attempted, setAttempted] = useState(false);

  const errors = validateBudgetEntryForm({
    layer,
    amount,
    period_start: period.period_start,
    period_end: period.period_end,
  });
  const show = (field: keyof BudgetEntryFormErrors) =>
    attempted || touched[field] ? errors[field] : undefined;
  const hasErrors = Object.keys(errors).length > 0;
  const touch = (field: string) => setTouched((t) => ({ ...t, [field]: true }));

  function submit() {
    setAttempted(true);
    if (hasErrors || create.isPending) return;
    create.mutate(
      {
        layer,
        amount: Number(amount),
        description: description || null,
        // Periods belong to actual spend; a change order carries none.
        ...(layer === 'actual_spend'
          ? { period_start: period.period_start, period_end: period.period_end }
          : {}),
      },
      {
        onSuccess: () => {
          setAmount('');
          setDescription('');
          setPeriod(currentMonthPeriod());
          setTouched({});
          setAttempted(false);
        },
      },
    );
  }

  return (
    <section
      aria-labelledby="budget-entry-title"
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
    >
      <h2 id="budget-entry-title" className="t-h3">
        Record spend / change order
      </h2>
      <div className="mt-3 flex flex-wrap items-start gap-3">
        <label className="text-body-sm">
          <span className="mb-1 block text-meta text-ink-500">Layer</span>
          <select
            value={layer}
            onChange={(e) => setLayer(e.target.value as typeof layer)}
            className="h-input-h rounded-card border border-ink-200 bg-paper-0 px-2"
          >
            <option value="actual_spend">Actual spend</option>
            <option value="change_order_delta">Change-order delta</option>
          </select>
        </label>
        {/* Each error sits beside its label, not inside it, so the label's
            accessible name stays the field name while the message shows. */}
        <div className="text-body-sm">
          <label>
            <span className="mb-1 block text-meta text-ink-500">Amount ($)</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={() => touch('amount')}
              aria-invalid={Boolean(show('amount'))}
              aria-describedby={show('amount') ? 'budget-entry-amount-error' : undefined}
              className={`h-input-h w-40 rounded-card border bg-paper-0 px-2 ${
                show('amount') ? inputErrorClass : 'border-ink-200'
              }`}
            />
          </label>
          <FieldError id="budget-entry-amount-error" message={show('amount')} />
        </div>
        {layer === 'actual_spend' ? (
          <>
            <div className="text-body-sm">
              <label>
                <span className="mb-1 block text-meta text-ink-500">Period start</span>
                <input
                  type="date"
                  value={period.period_start}
                  onChange={(e) => setPeriod((p) => ({ ...p, period_start: e.target.value }))}
                  onBlur={() => touch('period_start')}
                  aria-invalid={Boolean(show('period_start'))}
                  aria-describedby={show('period_start') ? 'budget-entry-start-error' : undefined}
                  className={`h-input-h rounded-card border bg-paper-0 px-2 ${
                    show('period_start') ? inputErrorClass : 'border-ink-200'
                  }`}
                />
              </label>
              <FieldError id="budget-entry-start-error" message={show('period_start')} />
            </div>
            <div className="text-body-sm">
              <label>
                <span className="mb-1 block text-meta text-ink-500">Period end</span>
                <input
                  type="date"
                  value={period.period_end}
                  onChange={(e) => setPeriod((p) => ({ ...p, period_end: e.target.value }))}
                  onBlur={() => touch('period_end')}
                  aria-invalid={Boolean(show('period_end'))}
                  aria-describedby={show('period_end') ? 'budget-entry-end-error' : undefined}
                  className={`h-input-h rounded-card border bg-paper-0 px-2 ${
                    show('period_end') ? inputErrorClass : 'border-ink-200'
                  }`}
                />
              </label>
              <FieldError id="budget-entry-end-error" message={show('period_end')} />
            </div>
          </>
        ) : null}
        <label className="min-w-56 flex-1 text-body-sm">
          <span className="mb-1 block text-meta text-ink-500">Description</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={inputClass}
            placeholder="e.g. April invoice / CO-2 scope add"
          />
        </label>
        <Button
          type="button"
          size="sm"
          className="mt-5"
          disabled={create.isPending || (attempted && hasErrors)}
          onClick={submit}
        >
          {create.isPending ? <Spinner /> : null}
          Add entry
        </Button>
      </div>
      {create.error ? <ErrorMessage error={create.error} className="mt-2" /> : null}
    </section>
  );
}
